/**
 * Formatos e contas da tela do banco de horas.
 *
 * Papel: aqui ficam as formas como o resumo, o detalhe, as caixas e as folhas
 * escrevem tempo, dias e ajuste, a leitura de `HH:MM` da importação, a lista de
 * ativos em ordem de nome, o saldo acumulado mês a mês e as cores do saldo.
 *
 * Como usar: tempo na tela do banco de horas sai **só** por `formatarHoras` —
 * `00:00`, `12:30`, `-05:10`, sem `+` —, e dias só por `formatarDias`. São as
 * formas de `bhFmtTime` e `bhFmtDias` da origem, sem `+` no tempo positivo.
 *
 * O saldo é sempre `saldoEmMinutos`, do pacote compartilhado, e a faixa é
 * `faixaDoSaldo`: −4320 minutos (−72:00) ainda é `atencao`, amarelo, e −4321 já
 * é `critico`, vermelho — a mesma régua de `bhBalClass`.
 */
import {
  type ColaboradorBancoHoras,
  type LancamentoMensal,
  MINUTOS_POR_DIA,
  type faixaDoSaldo,
  saldoEmMinutos,
} from '@concierge/shared';

import { formatarData } from '@/shared/lib/formato';

/** As três faixas de saldo, como `faixaDoSaldo` as devolve. */
type Faixa = ReturnType<typeof faixaDoSaldo>;

/** A variante do `Badge` de cada faixa — `.pill.concluido`, `.pendente`, `.vencido` da origem. */
export const VARIANTE_DA_FAIXA: Record<Faixa, 'concluido' | 'pendente' | 'vencido'> = {
  positivo: 'concluido',
  atencao: 'pendente',
  critico: 'vencido',
};

/** A cor do número de cada faixa na folha do resumo — `bhBalColor` da origem. */
export const TOM_DA_FAIXA: Record<Faixa, 'text-sucesso' | 'text-aviso' | 'text-perigo'> = {
  positivo: 'text-sucesso',
  atencao: 'text-aviso',
  critico: 'text-perigo',
};

/**
 * Escreve um tempo em minutos como a origem: `HH:MM`, com sinal só no negativo.
 *
 * @param minutos - O tempo em minutos inteiros.
 * @returns `00:00` para zero; as horas com dois dígitos no mínimo.
 */
export function formatarHoras(minutos: number): string {
  if (minutos === 0 || Number.isNaN(minutos)) return '00:00';
  const absoluto = Math.abs(minutos);
  const horas = String(Math.floor(absoluto / 60)).padStart(2, '0');
  const resto = String(absoluto % 60).padStart(2, '0');
  return `${minutos < 0 ? '-' : ''}${horas}:${resto}`;
}

/**
 * Escreve um saldo em dias de trabalho, como a origem.
 *
 * @param minutos - O saldo em minutos.
 * @returns Uma casa decimal com vírgula, `+` só no positivo e ` dias` no fim —
 *   `+1,5 dias`, `0,0 dias`, `-3,2 dias`. O débito menor que meio décimo de dia
 *   sai `-0,0 dias`, como na origem.
 */
export function formatarDias(minutos: number): string {
  const dias = minutos / MINUTOS_POR_DIA;
  return `${dias > 0 ? '+' : ''}${dias.toFixed(1).replace('.', ',')} dias`;
}

/**
 * Escreve o ajuste de um mês.
 *
 * @param minutos - O ajuste em minutos.
 * @returns `—` quando é zero, e o tempo com o sinal à frente nos outros casos.
 */
export function formatarAjuste(minutos: number): string {
  if (minutos === 0) return '—';
  return `${minutos > 0 ? '+' : ''}${formatarHoras(minutos)}`;
}

/**
 * Converte um número que pode ter vindo de um texto ilegível.
 *
 * @param valor - O número, ou `undefined` quando a parte não existia.
 * @returns O número, ou zero no lugar do ausente e do `NaN`.
 */
function zeroSeIlegivel(valor: number | undefined): number {
  return valor === undefined || Number.isNaN(valor) ? 0 : valor;
}

/**
 * Lê um tempo escrito em `HH:MM`, como `bhParseTime` da origem.
 *
 * O vazio e `00:00` valem zero; a vírgula vale dois-pontos; o sinal à frente
 * vale para o tempo inteiro; a parte ilegível vale zero.
 *
 * @param texto - O tempo como está na célula ou no extrato.
 * @returns O tempo em minutos.
 */
export function lerHoras(texto: string): number {
  if (texto.trim() === '' || texto === '00:00') return 0;
  const limpo = texto.trim().replace(',', ':');
  const negativo = limpo.startsWith('-');
  const [horas, minutos] = limpo.replace('-', '').split(':').map(Number);
  return (negativo ? -1 : 1) * (zeroSeIlegivel(horas) * 60 + zeroSeIlegivel(minutos));
}

/**
 * Os colaboradores ativos, em ordem de nome.
 *
 * @param colaboradores - A lista inteira do banco de horas.
 * @returns Só os `ativo`, ordenados por `localeCompare` em `pt-BR`.
 */
export function ativosPorNome(
  colaboradores: readonly ColaboradorBancoHoras[],
): ColaboradorBancoHoras[] {
  return colaboradores
    .filter((colaborador) => colaborador.ativo)
    .sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR'));
}

/** Um mês com o saldo dele e o saldo acumulado até ele. */
export interface MesComAcumulado {
  mes: LancamentoMensal;
  saldoDoMes: number;
  acumulado: number;
}

/**
 * Os meses do mais antigo ao mais novo, com o saldo de cada um e o acumulado.
 *
 * @param meses - Os lançamentos de uma pessoa, em qualquer ordem.
 * @returns Uma linha por mês, em ordem crescente de competência.
 */
export function comAcumulado(meses: readonly LancamentoMensal[]): MesComAcumulado[] {
  let acumulado = 0;
  return [...meses]
    .sort((a, b) => a.competencia.localeCompare(b.competencia))
    .map((mes) => {
      const saldoDoMes = saldoEmMinutos([mes]);
      acumulado += saldoDoMes;
      return { mes, saldoDoMes, acumulado };
    });
}

/**
 * A cor de um saldo na tela do colaborador: verde a partir de zero, vermelho abaixo.
 *
 * @param minutos - O saldo do mês, o acumulado ou o atual.
 * @returns A classe de cor do token.
 */
export function corDoSaldo(minutos: number): 'text-sucesso' | 'text-perigo' {
  return minutos >= 0 ? 'text-sucesso' : 'text-perigo';
}

/**
 * A linha de função, departamento e admissão do colaborador.
 *
 * A tela a escreve em caixa alta (`toUpperCase`) e a folha, pelo CSS; o texto é
 * o mesmo.
 *
 * @param colaborador - Os três campos, com nulo no que falta.
 * @returns `Função · Departamento · Admissão DD/MM/AAAA`, com `—` no que falta.
 */
export function linhaDoColaborador(
  colaborador: Pick<ColaboradorBancoHoras, 'funcao' | 'departamento' | 'admissao'>,
): string {
  return `${colaborador.funcao ?? '—'} · ${colaborador.departamento ?? '—'} · Admissão ${formatarData(colaborador.admissao)}`;
}
